import React, { useContext, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../../../styles'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import PropertyConcerned from '../../../../components/modalStepper/step/step0/PropertyConcerned'
import RequestType from './step1/RequestType'
import RequestPurpose from './step2/RequestPurpose'
import RequestConcern from './step3/RequestConcern'
import RequestSpecifications from './step4/RequestSpecifications'
import { ContractRequestModel, ContractsModel, ContractsOmission } from '../../../../types/contracts/contractsModel'
import { MakeRequestContext } from '../MakeRequestContext'
import RequestSummary from './step5/RequestSummary'
import { useMediaQuery } from 'react-responsive'
import { modalTypes } from '../../../../enums/modalTypeEnum'
import { saveInteractions } from '../../../gtm/utils/gtmCallback'
import { propertyFromApi, propertyType, requestPurpose } from '../../../../enums/contractsEnum'
import { contractRequestMapper } from '../../../contracts/contractMapper'
import RequestSpecificationsDeposit from './step4-2/RequestSpecificationsDeposit'
import PrelevementSummary from '../../../profile/prelevement/stepper/step3/PrelevementSummary'
import { SignResponseModel } from '../../../profile/profileModel'
import { fromEnum } from '../../../profile/prelevement/stepper/step3/enums/fromEnum'
import { estUnContractHopper } from '../../../../utils/estContractHopper'
import { EDLProvider } from '../../../dashboard/edl/context/EDLContext'

const useStyle = makeStyles(
	(theme) => (
		{
			...styles(theme),
			stepperLargeScreenStyle: {
				width: '60%',
				maxWidth: 900,
				margin: '25px auto 0'
			},
			stepperSmallScreenStyle: {
				marginTop: 40
			},
			breadcrumbStyle: {
				marginBottom: 55,
				color: theme.palette.blue['30']
			},
			descriptionStyle: {
				marginBottom: 40,

				'& > span:first-child, & img': {
					marginRight: 15
				}
			},
			descriptionStyleStepHigher: {
				marginTop: 105
			}
		}
	)
)

interface AcceptingProps {
	contracts: ContractRequestModel[],
	step: number,
	shouldSelectLease: boolean,
	stepNumber: number,
	signResponse?: SignResponseModel,
	signStatus?: string,
	setSignStatus: (value: string) => void
}

export const DEPOSIT_CONCERN = 'deposit'
export const ASSURANCE_CONCERN = 'assurance'

type StepperProps = AcceptingProps

const Stepper: React.FC<StepperProps> = (
	{
		contracts,
		step,
		shouldSelectLease,
		stepNumber,
		signResponse,
		signStatus,
		setSignStatus
	}
) => {

	const classes = useStyle()
	const { closeDialog, resetStep, nextStep, request, setStep } = useContext(MakeRequestContext)
	let stepDescriptionId: string
	if (step === 3 && request.requestConcern === DEPOSIT_CONCERN) {
		stepDescriptionId = `requests.makeRequest.step${step}.titleDeposit`
	} else if (step === 2 && request.requestPurpose === requestPurpose.private) {
		stepDescriptionId = `requests.makeRequest.step${step}.titlePrivate`
	} else {
		stepDescriptionId = `requests.makeRequest.step${step}.title`
	}
	const [interactionDone, setInteractionDone] = React.useState<boolean>(false)

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const descriptionClass: ClassValue = classNames(
		classes.flex,
		classes.descriptionStyle,
		{
			[classes.descriptionStyleStepHigher]: step < 1 && isLargeScreen,
			[classes.alignCenter]: isLargeScreen,
			[classes.flex]: !isLargeScreen,
			[classes.flexColumn]: !isLargeScreen
		},
		classes.colorBlue
	)

	const breadcrumbClass: ClassValue = classNames(
		classes.text0,
		classes.breadcrumbStyle
	)

	const stepperClass: ClassValue = classNames(
		{
			[classes.stepperLargeScreenStyle]: isLargeScreen,
			[classes.stepperSmallScreenStyle]: !isLargeScreen
		}
	)

	useEffect(
		() => {
			if (contracts.length === 1) {
				request.contract = contracts.map((contract) => contractRequestMapper(contract))[0]
				request.propertyConcerned = contracts[0].eft_Refbail.eft_bailid
				request.residence = contracts[0].eft_Refbail.eft_Referenceimmeuble.eft_bienid
				request.interlocutor = contracts[0].eft_interlocuteurid

				if (!interactionDone) {
					saveInteractions(
						{
							category: 'Vos Demandes',
							action: 'Déposer une demande',
							label: `Etape 1::Bien concerné::${contracts[0].eft_Refbail.type_bien === propertyType.apartment ? 'Appartement' : 'Parking'}`
						}
					)
					setInteractionDone(true)
				}
			}
		}, [contracts, request.contract, request.interlocutor, request.propertyConcerned, request.residence, interactionDone]
	)

	useEffect(() => {
		if (request.requestConcern === ASSURANCE_CONCERN) {
			if (contracts.filter((contract) => contract.eft_Refbail.type_bien === propertyFromApi.apartment).length === 1) {
				const premierContrat = contracts.map((contract) => contractRequestMapper(contract))[0]
				request.contract = premierContrat
				request.propertyConcerned = premierContrat.id
				request.residence = premierContrat.residences[0]
				request.interlocutor = premierContrat.interlocutor
				if (step < 4) {
					setStep(4)
				}
			} else if (request.propertyConcerned === '') {
				setStep(3)
			}
		}
	})

	const updatePropertyConcerned = (contract: Omit<ContractsModel, ContractsOmission> | Omit<ContractsModel, ContractsOmission>[], interlocutor: string = '') => {
		request.contract = contract
		request.propertyConcerned = Array.isArray(contract) ? contract.map((parseContract) => parseContract.id) : contract.id
		request.residence = contracts[0].eft_Refbail.eft_Referenceimmeuble.eft_bienid
		request.interlocutor = interlocutor


		if (!Array.isArray(contract)) {
			saveInteractions(
				{
					category: 'Vos Demandes',
					action: 'Déposer une demande',
					label: `Etape 1::Bien concerné::${contracts.filter((filteredContract) => filteredContract.eft_Refbail.eft_bailid === contract.id)[0].eft_Refbail.type_bien === propertyType.apartment ? 'Appartement' : 'Parking'}`
				}
			)
		}

		nextStep()
	}

	const getComposant = () => {
		switch (step) {
			case 0:
				return <RequestType />
			case 1:
				return <RequestPurpose shouldSelectLease={shouldSelectLease} />
			case 2:
				return <RequestConcern />
			case 3:
				if (request.requestConcern === ASSURANCE_CONCERN) {
					return <PropertyConcerned
						contractsMapped={contracts.filter((contract) => contract.eft_Refbail.type_bien === propertyFromApi.apartment).map((contract) => contractRequestMapper(contract))}
						updatePropertyConcerned={updatePropertyConcerned}
					/>
				}
				return <PropertyConcerned
					contractsMapped={contracts.filter((contract) => request.requestConcern === DEPOSIT_CONCERN ? !estUnContractHopper(contract.eft_Refbail.eft_Referenceimmeuble.eft_portefeuillevente) : true).map((contract) => contractRequestMapper(contract))}
					updatePropertyConcerned={updatePropertyConcerned}
					multiSelect={request.requestConcern === DEPOSIT_CONCERN}
				/>
			case 4:
				if (request.requestConcern === ASSURANCE_CONCERN) {
					return <RequestSpecifications />
				}
				if (request.requestConcern === DEPOSIT_CONCERN) {
					return <RequestSpecificationsDeposit />
				}
				return <RequestSpecifications />
			case 5:
				if (request.requestConcern === DEPOSIT_CONCERN) {
					return <EDLProvider>
						<PrelevementSummary
							signResponse={signResponse}
							closeDialog={closeDialog}
							resetStep={resetStep}
							stepNumber={stepNumber}
							signStatus={signStatus}
							setSignStatus={setSignStatus}
							from={fromEnum.leaveDeposit}
						/>
					</EDLProvider>
				}
				return <RequestSummary
					title={
						<FormattedMessage
							id="requests.makeRequest.step5.title"
							defaultMessage="Votre demande a bien été envoyée."
							description="Summary title"
						/>
					}
					text={
						<FormattedMessage
							id="requests.makeRequest.step5.text"
							defaultMessage="Nous vous répondons dans les meilleurs délais"
							description="Summary text"
						/>
					}
					closeDialog={closeDialog}
					fromType={modalTypes.demandes}
					resetStep={resetStep}
				/>
			default:
				return <RequestType />
		}
	}

	const stepToDisplay = () => {
		if (step >= 3 && !shouldSelectLease) {
			return step
		}

		return step + 1
	}

	return (
		<div className={stepperClass}>
			{
				step < 5 &&
				<>
					{
						isLargeScreen &&
						<div className={breadcrumbClass}>
							{
								step >= 1 &&
								<>
								<span>
									<FormattedMessage
										id={`requests.makeRequest.subHeader.${request.requestType}`}
										defaultMessage="Demande technique"
										description="Subheader breadcrumb"
									/>
								</span>

									{
										request.requestPurpose &&
										<span> - <FormattedMessage
											id={`requests.makeRequest.step2.${request.requestPurpose}`}
											defaultMessage="Parties privatives"
											description="Subheader breadcrumb"
										/>
									</span>
									}

									{
										request.requestConcern &&
										<span> - <FormattedMessage
											id={`requests.makeRequest.step3.${request.requestConcern}`}
											defaultMessage="Plomberie"
											description="Subheader breadcrumb"
										/>
									</span>
									}
								</>
							}
						</div>
					}

					{!(step % 1) ?
						<div className={descriptionClass}>
							<span>
								<span className={classes.text11}>
									{stepToDisplay()}
								</span>

								<span className={classes.text11_opacity}>
									/{stepNumber}
								</span>
							</span>

							{
								isLargeScreen &&
								<img src="img/requests/longArrow.svg" alt="Long arrow" />
							}

							<span className={classes.text11}>
								<FormattedMessage
									id={stepDescriptionId}
									defaultMessage="Quel est le bien concerné ?"
									description="Step description"
								/>
							</span>
						</div>
						:
						<></>
					}
				</>
			}

			<div>
				{
					React.createElement(getComposant)
				}
			</div>
		</div>
	)
}

export default Stepper
